import { Add, Subtract, WarningAlt } from '@carbon/icons-react';
import { useRef, useState, type ReactNode } from 'react';
import { CdButton } from '../../atoms/CdButton';
import { CdIconButton } from '../../atoms/CdIconButton';
import { CdSpinner } from '../../atoms/CdSpinner';
import { CdEmptyState } from '../../molecules/CdEmptyState';
import { cx } from '../../utils';
import { ArcGisMapCanvas } from './ArcGisMapCanvas';
import styles from './CdMapPanel.module.scss';
import {
  DEFAULT_MAP_BASEMAP,
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_MIN_ZOOM,
  DEFAULT_MAP_ZOOM,
} from './mapDefaults';
import type { MapAdapter, MapPoint } from './types';

export interface CdMapPanelProps {
  points: MapPoint[];
  selectedId?: string;
  onSelect?: (id: string) => void;
  status?: 'ready' | 'loading' | 'error';
  onRetry?: () => void;
  /** Overlay content positioned top-left (e.g. the search/filter card in map view). */
  overlay?: ReactNode;
  label?: string;
  loadingLabel?: string;
  className?: string;
  /** Initial centre. Longitude/latitude WGS84. Defaults to Washington, DC. */
  center?: { lat: number; lng: number };
  /** Initial zoom level. */
  zoom?: number;
  /** Floor the user can zoom out to. */
  minZoom?: number;
  /** Esri basemap id (e.g. `topo-vector`). */
  basemap?: string;
}

/** ArcGIS map of result pins, with Carbon zoom controls and an error/retry state. */
export const CdMapPanel = ({
  points,
  selectedId,
  onSelect,
  status = 'ready',
  onRetry,
  overlay,
  label = 'Map of results',
  loadingLabel = 'Loading map',
  className,
  center = DEFAULT_MAP_CENTER,
  zoom = DEFAULT_MAP_ZOOM,
  minZoom = DEFAULT_MAP_MIN_ZOOM,
  basemap = DEFAULT_MAP_BASEMAP,
}: CdMapPanelProps) => {
  const adapterRef = useRef<MapAdapter | null>(null);
  const [ready, setReady] = useState(false);
  const [internalError, setInternalError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const failed = status === 'error' || internalError;
  const showLoading = !failed && (status === 'loading' || !ready);

  const handleRetry = () => {
    setInternalError(false);
    setReady(false);
    adapterRef.current = null;
    setRetryKey((key) => key + 1);
    onRetry?.();
  };

  if (failed) {
    return (
      <div className={cx(styles.root, styles.error, className)}>
        <CdEmptyState
          icon={WarningAlt}
          size="page"
          title="The map couldn't load"
          description="Check your connection and try again. Results are still available in list view."
          action={
            onRetry != null || internalError ? (
              <CdButton variant="tertiary" size="sm" onClick={handleRetry}>
                Try again
              </CdButton>
            ) : undefined
          }
        />
      </div>
    );
  }

  return (
    <section className={cx(styles.root, className)} aria-label={label} aria-busy={showLoading}>
      <ArcGisMapCanvas
        key={retryKey}
        className={styles.canvas}
        points={points}
        selectedId={selectedId}
        onSelect={onSelect}
        center={center}
        zoom={zoom}
        minZoom={minZoom}
        basemap={basemap}
        onReady={(adapter) => {
          adapterRef.current = adapter;
          setReady(true);
        }}
        onDestroyed={() => {
          adapterRef.current = null;
          setReady(false);
        }}
        onError={() => setInternalError(true)}
      />
      {showLoading && (
        <div className={styles.loading}>
          <CdSpinner label={loadingLabel} />
        </div>
      )}
      {overlay && <div className={styles.overlay}>{overlay}</div>}
      <div className={styles.zoom}>
        <CdIconButton
          label="Zoom in"
          icon={Add}
          variant="ghost"
          align="left"
          onClick={() => adapterRef.current?.zoomIn()}
        />
        <CdIconButton
          label="Zoom out"
          icon={Subtract}
          variant="ghost"
          align="left"
          onClick={() => adapterRef.current?.zoomOut()}
        />
      </div>
    </section>
  );
};
